import type { FunctionComponent, ReactNode } from "react";
import { useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { itemSearchFollowTheTimelineArticleSearchResultStreamItemTimelineSummary$key } from "scmp-app/queries/__generated__/itemSearchFollowTheTimelineArticleSearchResultStreamItemTimelineSummary.graphql";

import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledArrowIcon,
  Title,
} from "./styles";

type Props = {
  children?: (expand: boolean, setExpand: (value: boolean) => void) => ReactNode;
  index: number;
  reference: itemSearchFollowTheTimelineArticleSearchResultStreamItemTimelineSummary$key;
};

export const SearchFollowTheTimelineItem: FunctionComponent<Props> = ({
  children,
  index,
  reference,
}) => {
  const item = useFragment(
    graphql`
      fragment itemSearchFollowTheTimelineArticleSearchResultStreamItemTimelineSummary on ArticleSearchResultStreamItemTimelineSummary {
        id
        humanReadableDate
        summary
      }
    `,
    reference,
  );

  const [expand, setExpand] = useState(false);
  const { currentValue } = useSearchContext();

  const handleChange = useCallback(
    (_: unknown, isExpanded: boolean) => {
      if (!isExpanded) return;
      // GA4 search_expand_click
      sendGA4Tracking({
        action: "click",
        category: "search",
        customized_parameters: {
          search_text: currentValue,
          widget_category_name: item.summary,
          widget_category_position: index + 1,
          widget_name: "Follow the timeline",
        },
        subcategory: "expand",
      } as SearchEvent);
    },
    [currentValue, index, item.summary],
  );

  return (
    <StyledAccordion defaultExpanded disableGutters elevation={0} onChange={handleChange}>
      <StyledAccordionSummary expandIcon={<StyledArrowIcon />} id={item.id}>
        <Title>
          <time>{item.humanReadableDate}</time>
          <span>•</span>
          <div>{item.summary}</div>
        </Title>
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children?.(expand, setExpand)}</StyledAccordionDetails>
    </StyledAccordion>
  );
};

SearchFollowTheTimelineItem.displayName = "SearchFollowTheTimelineItem";
